import React, { useCallback, useEffect, useRef, useState } from 'react';
import templatesService from '../../../../services/templates.service';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import { showError } from '../../../../utils/ecxSwal';
import { ClinicInfoDetailItem, ClinicInfoPanel } from '../../settings/clinicsettings/info/clinicInfoParts';
import ClinicSettingsEmpty from '../../settings/clinicsettings/subparts/ClinicSettingsEmpty';
import ClinicSettingsListSkeleton from '../../settings/clinicsettings/subparts/ClinicSettingsListSkeleton';
import TemplateTypeList from '../TemplateTypeList';
import { formatReminderLabel, groupTemplatesByType } from '../templateTypeGroups';

const isUnresolvedStoragePath = (content) => {
    if (!content || typeof content !== 'string') {
        return false;
    }
    const trimmed = content.trim();
    if (trimmed.startsWith('<')) {
        return false;
    }
    return (
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('s3://')
    );
};

const EmailTemplates = () => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const [listLoading, setListLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const hasAutoSelected = useRef(false);

    const htmlContent = selectedTemplate?.content || '';
    const contentUnavailable = isUnresolvedStoragePath(htmlContent);

    const loadTemplateById = useCallback(
        async (id) => {
            if (!id || !clinicId) return;

            setSelectedId(id);
            setDetailLoading(true);

            try {
                // By-id endpoint resolves private S3 HTML server-side
                const res = await templatesService.getEmailTemplateById(clinicId, id);
                setSelectedTemplate(res.data);
            } catch (err) {
                console.error(err);
                setSelectedTemplate(null);
                showError({
                    title: 'Load Failed',
                    text: 'Unable to load this email template. Please try again.',
                });
            } finally {
                setDetailLoading(false);
            }
        },
        [clinicId]
    );

    const loadTemplates = useCallback(async () => {
        if (!clinicId) return;

        setListLoading(true);
        try {
            const res = await templatesService.getEmailTemplates(clinicId);
            const items = res.data || [];
            setTemplates(items);

            if (items.length > 0 && !hasAutoSelected.current) {
                hasAutoSelected.current = true;
                const firstTemplate = groupTemplatesByType(items)[0]?.templates[0];
                if (firstTemplate) {
                    await loadTemplateById(firstTemplate.id);
                }
            }
        } catch (err) {
            console.error(err);
            setTemplates([]);
            showError({
                title: 'Load Failed',
                text: 'Unable to load email templates. Please try again.',
            });
        } finally {
            setListLoading(false);
        }
    }, [clinicId, loadTemplateById]);

    useEffect(() => {
        hasAutoSelected.current = false;
        loadTemplates();
    }, [loadTemplates]);

    return (
        <div className="row g-3 templates-layout">
            <div className="col-12 col-lg-3 templates-list-sidebar">
                <ClinicInfoPanel
                    title="Template types"
                    icon="bi-list-ul"
                    actions={
                        !listLoading && templates.length > 0 ? (
                            <span className="ecx-page-chip">
                                <i className="bi bi-envelope"></i>
                                {templates.length}
                            </span>
                        ) : null
                    }
                >
                    {listLoading ? (
                        <ClinicSettingsListSkeleton rows={5} />
                    ) : templates.length === 0 ? (
                        <ClinicSettingsEmpty
                            icon="bi-envelope"
                            title="No email templates"
                            description="No email templates are configured for this clinic."
                        />
                    ) : (
                        <TemplateTypeList
                            templates={templates}
                            selectedId={selectedId}
                            onSelect={loadTemplateById}
                            itemIcon="bi-envelope"
                            showStatus={false}
                        />
                    )}
                </ClinicInfoPanel>
            </div>

            <div className="col-12 col-lg-9">
                {!selectedId ? (
                    <ClinicSettingsEmpty
                        icon="bi-envelope"
                        title="Select a template"
                        description="Choose a template type from the list to preview its email content."
                    />
                ) : detailLoading ? (
                    <ClinicInfoPanel title="Loading template…" icon="bi-envelope">
                        <ClinicSettingsListSkeleton rows={4} />
                    </ClinicInfoPanel>
                ) : (
                    <>
                        <div className="templates-readonly-banner">
                            <span className="templates-readonly-banner__icon">
                                <i className="bi bi-eye"></i>
                            </span>
                            <div>
                                <h6 className="templates-readonly-banner__title">View only</h6>
                                <p className="templates-readonly-banner__text mb-0">
                                    Email templates are managed by EyeconX. Contact support if you
                                    need changes to an email template.
                                </p>
                            </div>
                        </div>

                        <ClinicInfoPanel
                            title={
                                formatReminderLabel(selectedTemplate?.reminderName) ||
                                'Email template'
                            }
                            icon="bi-envelope"
                        >
                            <div className="templates-detail-meta">
                                <ClinicInfoDetailItem label="Template name">
                                    {formatReminderLabel(selectedTemplate?.reminderName)}
                                </ClinicInfoDetailItem>

                                <ClinicInfoDetailItem label="Subject">
                                    {selectedTemplate?.subjectLine || '—'}
                                </ClinicInfoDetailItem>
                            </div>

                            <div className="templates-field-group">
                                <div className="templates-field-group__header">
                                    <label className="templates-field-group__label">
                                        Email preview
                                    </label>
                                </div>

                                <div className="templates-preview-card templates-email-preview-card">
                                    <div className="templates-preview-card__header">
                                        <i className="bi bi-envelope-open"></i>
                                        Rendered HTML
                                    </div>
                                    <div className="templates-preview-card__body templates-email-preview-card__body">
                                        {contentUnavailable ? (
                                            <div className="templates-email-preview-empty">
                                                <i className="bi bi-exclamation-triangle"></i>
                                                <p className="mb-0">
                                                    Template content could not be loaded from
                                                    storage.
                                                </p>
                                            </div>
                                        ) : (
                                            <iframe
                                                title={`Preview of ${
                                                    formatReminderLabel(
                                                        selectedTemplate?.reminderName
                                                    ) || 'email template'
                                                }`}
                                                srcDoc={
                                                    htmlContent ||
                                                    '<p style="font-family:sans-serif;color:#6c757d;">No content available.</p>'
                                                }
                                                sandbox="allow-same-origin"
                                                className="templates-email-preview-frame"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="templates-detail-footer">
                                <span className="templates-detail-footer__updated">
                                    <i className="bi bi-clock"></i>
                                    Last updated {selectedTemplate?.modifiedAt || '—'}
                                </span>
                            </div>
                        </ClinicInfoPanel>
                    </>
                )}
            </div>
        </div>
    );
};

export default EmailTemplates;
